import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import {
    VStack,
    HStack,
    Text,
    Input,
    Button,
    IconButton,
    Radio,
    RadioGroup,
    Box,
} from '@chakra-ui/react';
import { Plus, Trash2, Pencil, Check } from 'lucide-react';
import { useAppStore } from '../../state/store';
import { ManageDialogFrame, ManageDialogCloseFooter, ManageDialogPrimaryButton } from './ManageDialogFrame';
import { useBIMStore } from '../../state/useBIMStore';

export function PhasingDialog() {
    const { t } = useTranslation();
    const isOpen = useAppStore((state) => state.manageDialog === 'phasing');
    const onClose = useAppStore((state) => state.closeManageDialog);
    const project = useBIMStore((state) => state.project);
    const selection = useBIMStore((state) => state.selection);
    const addPhase = useBIMStore((state) => state.addPhase);
    const renamePhase = useBIMStore((state) => state.renamePhase);
    const removePhase = useBIMStore((state) => state.removePhase);
    const setActivePhase = useBIMStore((state) => state.setActivePhase);
    const assignPhaseToElements = useBIMStore((state) =>state.assignPhaseToElements); const [newPhaseName, setNewPhaseName] = useState(''); const [editingId, setEditingId] = useState<string | null>(null);
    const [editingName, setEditingName] = useState('');

    const handleAdd = () => {
        const name = newPhaseName.trim();
        if (!name) return;
        addPhase(name);
        setNewPhaseName('');
    };

    const handleStartRename = (id: string, currentName: string) => {
        setEditingId(id);
        setEditingName(currentName);
    };

    const handleCommitRename = () => {
        if (editingId && editingName.trim()) {
            renamePhase(editingId, editingName.trim());
        }
        setEditingId(null);
        setEditingName('');
    };

    const handleAssign = () => {
        const phase = project.phases.find((p) => p.id === project.activePhaseId);
        if (!phase || selection.length === 0) return;
        assignPhaseToElements(selection, phase.id);
    };

    return (
        <ManageDialogFrame
            title={t('manage.phasing')}
            isOpen={isOpen}
            onClose={onClose}
            footer={
                <ManageDialogCloseFooter
                    onClose={onClose}
                    extra={
                        <ManageDialogPrimaryButton
                            label={selection.length > 0 ? `Assign ${selection.length} Element(s)` : 'Assign Selection'}
                            onClick={handleAssign}
                            isDisabled={selection.length === 0}
                        />
                    }
                />
            }
        >
            <VStack spacing={4} align="stretch">
                <Text fontSize="xs" color="var(--color-text-secondary)">{t('manage.phasesOrderConstructionDemolitionSequencingTheActivePhaseReceivesNewly')}</Text>

                <RadioGroup
                    value={project.activePhaseId}
                    onChange={(value) => setActivePhase(value)}
                >
                    <VStack align="stretch" spacing={1}>
                        {[...project.phases]
                            .sort((a, b) => a.sequence - b.sequence)
                            .map((phase) => {
                                const phaseCount = Array.from(project.elements.values()).filter(
                                    (el) =>el.phaseCreatedId === phase.id ).length; return (<Box
                                        key={phase.id}
                                        p={2}
                                        borderRadius="var(--cad-radius-sm)"
                                        borderWidth="1px"
                                        borderColor={
                                            project.activePhaseId === phase.id
                                                ? 'var(--color-accent)'
                                                : 'var(--color-border)'
                                        }
                                    >
                                        <HStack justify="space-between">
                                            <HStack spacing={2} minWidth={0} flex="1 1 auto">
                                                <Radio value={phase.id} colorScheme="blue" size="sm" flexShrink={0} />
                                                {editingId === phase.id ? (
                                                    <Input
                                                        size="xs"
                                                        value={editingName}
                                                        onChange={(e) => setEditingName(e.target.value)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') handleCommitRename();
                                                            if (e.key === 'Escape') setEditingId(null);
                                                        }}
                                                        bg="var(--color-bg-tertiary)"
                                                        borderColor="var(--color-border)"
                                                        autoFocus
                                                    />
                                                ) : (
                                                    <Text
                                                        fontSize="sm"
                                                        isTruncated
                                                        flexShrink={0}
                                                    >
                                                        {phase.name}
                                                    </Text>
                                                )}
                                                <Text
                                                    fontSize="xs"
                                                    color="var(--color-text-secondary)"
                                                    flexShrink={0}
                                                >
                                                    ({phaseCount} element{phaseCount === 1 ? '' : 's'})
                                                </Text>
                                            </HStack>
                                            <HStack spacing={1} flexShrink={0}>
                                                {editingId === phase.id ? (
                                                    <IconButton
                                                        aria-label={t('manage.confirmRename')}
                                                        icon={<Check size={12} />}
                                                        size="xs"
                                                        variant="ghost"
                                                        onClick={handleCommitRename}
                                                    />
                                                ) : (
                                                    <IconButton
                                                        aria-label={`Rename ${phase.name}`}
                                                        icon={<Pencil size={12} />}
                                                        size="xs"
                                                        variant="ghost"
                                                        onClick={() => handleStartRename(phase.id, phase.name)}
                                                    />
                                                )}
                                                <IconButton
                                                    aria-label={`Delete ${phase.name}`}
                                                    icon={<Trash2 size={12} />}
                                                    size="xs"
                                                    variant="ghost"
                                                    isDisabled={project.phases.length <= 1}
                                                    onClick={() => removePhase(phase.id)}
                                                />
                                            </HStack>
                                        </HStack>
                                    </Box>
                                );
                            })}
                    </VStack>
                </RadioGroup>

                <HStack spacing={2}>
                    <Input
                        size="sm"
                        placeholder={t('manage.newPhaseName')}
                        value={newPhaseName}
                        onChange={(e) => setNewPhaseName(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') handleAdd();
                        }}
                        bg="var(--color-bg-tertiary)"
                        borderColor="var(--color-border)"
                    />
                    <Button
                        size="sm"
                        leftIcon={<Plus size={14} />}
                        onClick={handleAdd}
                        isDisabled={!newPhaseName.trim()}
                        variant="ghost"
                    >{t('manage.addPhase')}</Button>
                </HStack>
            </VStack>
        </ManageDialogFrame>
    );
}
